import { Modal, TextInput, Stack, Box, Title, Select, Button } from '@mantine/core';
import { Icon } from '@common/components/foundations/icons';
import { useAuthStore } from '@common/store/auth';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as z from 'zod';
import { useUpdateAdminUser } from '@pages/Admin/RpgUsers/hooks/useUpdateAdminUser';
import { ModalFooter } from '@common/mantine/components/modal-footer';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onClick?: () => void;
  children?: React.ReactNode;
  selectedUser?: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    is_sales: boolean;
    sub_account_type: number | null;
  };
};

const schema = z.object({
  token: z.string().or(z.undefined()).nullable(),
  id: z.number().or(z.string()).nullable().optional(),
  username: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  is_sales: z.boolean().optional(),
  sub_account_type: z.number().or(z.string()).nullable().optional()
});

export const UpdateAdminUserModal = ({ isOpen, onClose, selectedUser }: ModalProps) => {
  const { token } = useAuthStore();
  const { updateAdminUser } = useUpdateAdminUser();

  const {
    control,
    reset,
    handleSubmit,
    formState: { isValid }
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema)
  });

  useEffect(() => {
    if (selectedUser) {
      reset({
        username: selectedUser?.email,
        ...selectedUser,
        is_sales: selectedUser?.sub_account_type === 1 ? true : false
      });
    }
  }, [selectedUser]);

  const onSubmit = (data: any) => {
    if (selectedUser?.id) {
      const payloadData = {
        payload: {
          token,
          ...data
        },
        id: selectedUser?.id
      };
      updateAdminUser(payloadData);
    }
    onClose();
    reset();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={() => {
        onClose();
        reset();
      }}
      title={`Edit Admin User : ${selectedUser?.first_name} ${selectedUser?.last_name}`}
      size="xl"
      styles={{ body: { padding: 0 } }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack p="md">
          <Box className="flex w-full justify-between gap-4">
            <Box className="w-1/2">
              <Controller
                name="first_name"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextInput
                    label="First Name *"
                    variant="filled"
                    placeholder="Please enter first name"
                    value={value}
                    onChange={onChange}
                    error={error?.message}
                  />
                )}
              />
            </Box>
            <Box className="w-1/2">
              <Controller
                name="last_name"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextInput
                    label="Last Name *"
                    variant="filled"
                    placeholder="Please enter last name"
                    value={value}
                    onChange={onChange}
                    error={error?.message}
                  />
                )}
              />
            </Box>
          </Box>
          <Controller
            name="username"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextInput
                label="Email *"
                variant="filled"
                placeholder="Please enter email"
                value={value}
                onChange={onChange}
                error={error?.message}
                type="email"
              />
            )}
          />
          <Controller
            name="sub_account_type"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Select
                label="Team"
                variant="filled"
                placeholder="Select Team"
                value={value?.toString()}
                onChange={(val) => onChange(val ? parseInt(val) : null)}
                error={error?.message}
                data={[
                  { label: 'Not Selected', value: '' },
                  { label: 'Sales', value: '1' },
                  { label: 'Support', value: '3' }
                ]}
              />
            )}
          />
        </Stack>
        <ModalFooter>
          <Button
            variant="subtle"
            onClick={() => {
              onClose();
              reset();
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={!isValid} onClick={handleSubmit(onSubmit)}>
            Save
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
